import React from "react";
import {
  ActionIcon,
  Box,
  Collapse,
  Fieldset,
  Group,
  Stack,
  Switch,
  Text,
} from "@mantine/core";
import { IconChevronDown, IconChevronUp } from "@tabler/icons-react";
import StaffServiceSettingsFields from "../StaffServiceSettings/StaffServiceSettings";
import WeekdayToggle from "../ServiceFormWizard/WeekdayToggle";
import { extractDayFlags } from "../ServiceFormWizard/dayAvailability";
import { formatCurrency } from "../../../utils/cashDrawerUtils";
import type { SalonService } from "../../../types/salon";
import type { StaffServiceAssignment } from "../../../types/staffForm";
import {
  formatDurationMinutes,
  mergeServiceSettings,
} from "./staffServiceUtils";
import styles from "./StaffForm.module.css";

interface MenuServiceRowProps {
  service: SalonService;
  assignment: StaffServiceAssignment;
  onChange: (next: StaffServiceAssignment) => void;
  /** Read-only: the toggle and fields are disabled (the row can still expand). */
  disabled: boolean;
  isMobile: boolean;
}

/**
 * One service on the Menu tab: a switch to offer it, a one-line summary of this
 * staff member's price/duration, and (once offered) expandable per-staff
 * settings — durations, price, online booking and available days.
 */
export function MenuServiceRow({
  service,
  assignment,
  onChange,
  disabled,
  isMobile,
}: MenuServiceRowProps) {
  const summary = `${formatDurationMinutes(assignment.duration)} • ${
    assignment.startsAt ? "Starts at " : ""
  }${formatCurrency(Number(assignment.price))}`;

  const toggleOffered = (checked: boolean) =>
    onChange({
      ...assignment,
      enabled: checked,
      // Open the settings when a service is first offered; collapse on removal.
      isOpen: checked,
    });

  return (
    <Box
      className={`${styles.serviceRow} ${
        assignment.enabled ? styles.serviceRowEnabled : ""
      }`}
    >
      <Group gap="sm" wrap="nowrap" align="center">
        <Switch
          size="md"
          color="teal"
          checked={assignment.enabled}
          disabled={disabled}
          aria-label={`Offer ${service.name}`}
          onChange={(event) => toggleOffered(event.currentTarget.checked)}
        />
        <Box flex={1} miw={0}>
          <Text
            size="sm"
            fw={600}
            c={assignment.enabled ? undefined : "dimmed"}
          >
            {service.name}
          </Text>
          <Text size="xs" c="dimmed">
            {summary}
          </Text>
        </Box>
        {assignment.enabled && (
          <ActionIcon
            variant="subtle"
            color="gray"
            size={44}
            aria-label={`Customize ${service.name}`}
            aria-expanded={assignment.isOpen}
            onClick={() =>
              onChange({ ...assignment, isOpen: !assignment.isOpen })
            }
          >
            {assignment.isOpen ? (
              <IconChevronUp size={18} />
            ) : (
              <IconChevronDown size={18} />
            )}
          </ActionIcon>
        )}
      </Group>

      {assignment.enabled && (
        <Collapse in={assignment.isOpen}>
          <Fieldset
            variant="unstyled"
            disabled={disabled}
            pt="sm"
            pl={{ base: 0, xs: 52 }}
          >
            <Stack gap="md">
              <StaffServiceSettingsFields
                settings={assignment}
                isMobile={isMobile}
                onChange={(settings) =>
                  onChange(mergeServiceSettings(assignment, settings))
                }
              />
              <WeekdayToggle
                value={extractDayFlags(assignment)}
                onChange={(flags) => onChange({ ...assignment, ...flags })}
                description="Days this staff member can be booked for this service."
                size={isMobile ? "md" : "sm"}
                disabled={disabled}
              />
            </Stack>
          </Fieldset>
        </Collapse>
      )}
    </Box>
  );
}
